import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Button,
  Autocomplete,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import ReusableDialog from "../../../../common/dialog/dialog";
import axios from "axios";
import { useSelector } from "react-redux";
import Loading from "../../../../common/loader/loading";

const keyNumbers = ["23", "24", "25", "26"];
const keyCategories = ["bedroom key ", "maingate key"];

const SignInSheet = ({ onClose, selectedTask, setOpenDefaultSnackbar }) => {
  const user = useSelector((state) => state.auth.user);
  const [records, setRecords] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // State to track if the form is in edit mode
  const currentTime = new Date().toTimeString().slice(0, 5); // Extracts HH:MM format
  const [newRecord, setNewRecord] = useState({
    key_no: "",
    date: "",
    key_category: "",
    pickup_time: currentTime,
    dropoff_time: "",
  });
  const [loading, setLoading] = useState(true);
  const submittedKeyNumbers = records.map((record) => record.key_no);
  const availableKeyNumbers = keyNumbers.filter((key) => !submittedKeyNumbers.includes(key));
  const todayDate = new Date().toISOString().split("T")[0]; // Format today's date as "YYYY-MM-DD"
  const [taskId, setTaskId] = useState(selectedTask?.id);

  useEffect(() => {
    if (!taskId && selectedTask?.id) {
      setTaskId(selectedTask.id);
    }
  }, [selectedTask?.id]); // Run only if `selectedTask.id` is not set

  const updateSubTask = async (taskId) => {
    if (!taskId) {
      console.error("Task ID is undefined. Cannot update.");
      return;
    }

    try {
      await axios.put(
        `/api/assessments/updateTask/${taskId}`,
        {
          status: "completed",
          checklist: [],
          updated_by: user?.username || "Unknown",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-role": user?.roleid || "",
          },
        }
      );
      setOpenDefaultSnackbar(true);
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  const fetchAllRecords = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/signin/homeKeyAll", {
        params: {
          employee_name: user.username,
        },
      });

      const filteredRecords = (response.data.records || []).filter((record) => {
        if (!record.date) return false; // Skip records with missing or invalid date

        const recordDate = new Date(record.date);
        if (isNaN(recordDate.getTime())) return false; // Skip invalid dates

        return recordDate.toLocaleDateString("en-CA") === todayDate;
      });

      setRecords(filteredRecords);
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setIsEditMode(false);
    setNewRecord({
      key_no: "",
      date: "",
      key_category: "",
      pickup_time: currentTime,
      dropoff_time: "",
    });
  };

  const handleEditSubmit = async () => {
    try {
      const payload = {
        dropoff_time: newRecord.dropoff_time,
      };

      const response = await axios.put(`api/signin/homeKey/${newRecord?.id}`, payload);

      if (response.data.success) {
        console.log("Record updated successfully:", response.data);

        // Update the local state with the updated record
        setRecords((prevRecords) =>
          prevRecords.map((record) => (record.id === newRecord.id ? { ...record, ...newRecord } : record))
        );

        // Close the dialog and reset form
        handleDialogClose();
      } else {
        console.error("Error updating record:", response.data.message);
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  const handleEdit = (record) => {
    // Format the date to "yyyy-MM-dd"

    // Populate the form with the selected record for editing
    setNewRecord({
      id: record.id, // Include the record ID for API
      key_no: record.key_no,
      date: record.date, // Use formatted date
      key_category: record.key_category,
      pickup_time: record.pickup_time,
      dropoff_time: record.dropoff_time,
    });
    setIsEditMode(true); // Enable edit mode
    setDialogOpen(true); // Open the dialog
  };

  const handleDialogSubmit = async () => {
    try {
      // Prepare the payload, excluding dropoff_time
      const { key_no, key_category, pickup_time } = newRecord;
      const payload = {
        key_no,
        date: todayDate,
        key_category, // Use the formatted date
        employee_name: user.username,
        pickup_time,
      };

      // Send POST request to the API
      const response = await axios.post("/api/signin/homeKey", payload);

      if (response.data.success) {
        console.log("Record added successfully:", response.data);
        // Add the new record to the local state
        setRecords([...records, newRecord]);

        // Reset and close the dialog
        handleDialogClose();
      } else {
        console.error("Error adding record:", response.data.message);
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setNewRecord((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Check if all fields are filled
  const isSubmitDisabled = !newRecord.key_no || !newRecord.key_category || !newRecord.pickup_time;

  if (loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: 4, m: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Tasks'
        size='small'
      />
      {/* Header */}
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 2 }}>
        Sign In Sheet For The Home Keys
      </Typography>
      <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
        Date: {todayDate} &nbsp; • &nbsp;
        <span style={{ color: "#1751D0" }}>Kindergarten Bedroom</span>
      </Typography>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Key No
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Key Category
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Date
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Sign Out
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Sign In
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records?.map((record, index) => (
            <TableRow key={index}>
              <TableCell>{record.key_no}</TableCell>
              <TableCell>{record.key_category}</TableCell>
              <TableCell>{new Date(record?.date).toLocaleDateString("en-CA")}</TableCell>
              <TableCell>{record.pickup_time}</TableCell>
              <TableCell sx={{ color: "green" }}>
                {record.dropoff_time ? (
                  <Grid container spacing={1}>
                    <CheckCircleIcon /> {record.dropoff_time}
                  </Grid>
                ) : (
                  <Button variant='outlined' color='error' onClick={() => handleEdit(record)} size='small'>
                    <AccessTimeFilledIcon />
                    Pending
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Add Record Button */}
      <Box sx={{ mt: 2, mb: 4 }}>
        <Button
          onClick={handleDialogOpen}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
          disabled={availableKeyNumbers.length === 0}
        >
          <AddIcon fontSize='small' /> Add another record
        </Button>
      </Box>

      {/* Add Record Dialog */}
      <ReusableDialog
        isOpen={dialogOpen}
        onClose={handleDialogClose}
        title={isEditMode ? "Sign In Time" : "Add a Sign-In Record"}
        onSubmit={isEditMode ? handleEditSubmit : handleDialogSubmit}
        submitText='Submit'
        cancelText='Cancel'
        disable={isSubmitDisabled}
      >
        <Grid container spacing={2}>
          {!isEditMode && (
            <>
              {/* Key Number Field */}
              <Grid item sm={3}>
                <Autocomplete
                  id='key_no'
                  options={availableKeyNumbers} // Use filtered key numbers
                  value={newRecord.key_no}
                  onChange={(event, newValue) => handleInputChange("key_no", newValue)}
                  renderInput={(params) => (
                    <>
                      <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                        Key Number
                      </Typography>
                      <TextField {...params} fullWidth />
                    </>
                  )}
                  fullWidth
                />
              </Grid>
            </>
          )}
          {!isEditMode && (
            <Grid item sm={12}>
              <Autocomplete
                id='key_category'
                options={keyCategories}
                value={newRecord.key_category}
                onChange={(event, newValue) => handleInputChange("key_category", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                      Key Category
                    </Typography>
                    <TextField {...params} placeholder='Select' fullWidth />
                  </>
                )}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
          )}

          {/* Pickup Time Field */}
          {!isEditMode && (
            <Grid item sm={4}>
              <Typography sx={{ fontSize: "14px", color: "black" }}>Sign In</Typography>
              <TextField
                fullWidth
                type='time'
                value={newRecord.pickup_time}
                onChange={(e) => {
                  const newPickUpTime = e.target.value;
                  handleInputChange("pickup_time", newPickUpTime);

                  // Ensure dropoff_time is reset if it is less than pickup_time
                  if (newRecord.dropoff_time && newPickUpTime >= newRecord.dropoff_time) {
                    handleInputChange("dropoff_time", "");
                  }
                }}
                sx={{ mb: 2 }}
              />
            </Grid>
          )}
        </Grid>

        {/* Sign Out */}
        {isEditMode && (
          <Grid item sm={4} mt={2}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Set the Time</Typography>
            <TextField
              fullWidth
              type='time'
              value={newRecord.dropoff_time}
              onChange={(e) => {
                const newDropOffTime = e.target.value;

                // Only allow dropoff_time if it's greater than pickup_time
                if (newRecord.pickup_time && newDropOffTime <= newRecord.pickup_time) {
                  alert("Sign Out must be greater than Sign In.");
                } else {
                  handleInputChange("dropoff_time", newDropOffTime);
                }
              }}
              sx={{ mb: 2 }}
            />
          </Grid>
        )}
      </ReusableDialog>

      {/* Action Buttons */}
      <Grid item xs={12} mt={2}>
        <Grid container spacing={1} className='submit-button-container'>
          <Grid item>
            <Button
              variant='outlined'
              color='primary'
              className='submit-button'
              sx={{ textTransform: "none" }}
              onClick={() => onClose()}
            >
              Previous
            </Button>
          </Grid>
          <Grid item>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              className='submit-button'
              sx={{ textTransform: "none" }}
              onClick={() => {
                // Check if all records have a dropoff_time
                const allSignedOut = records.every(
                  (record) => record.dropoff_time !== null && record.dropoff_time !== ""
                );

                if (allSignedOut) {
                  updateSubTask(taskId); // Call updateSubTask if all dropoff_times are filled
                  onClose();
                } else {
                  onClose();
                }
              }}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SignInSheet;
